import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { Loader2, CheckCircle, Ban, AlertTriangle } from 'lucide-react';
import { AvatarDisplay } from '../../utils/avatars';
import { useModal } from '../../context/ModalContext';

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { showConfirm, showAlert } = useModal();

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('reports')
        .select(`
          *,
          reporter:profiles!reports_reporter_id_fkey(id, name, avatar),
          reported:profiles!reports_reported_user_id_fkey(id, name, avatar, is_banned)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (err) {
      console.error('Fetch reports error:', err);
      showAlert('錯誤', '無法載入檢舉列表');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleDismiss = async (reportId) => {
    showConfirm('忽略檢舉', '確定要忽略這個檢舉嗎？', async () => {
      try {
        const { error } = await supabase.rpc('admin_resolve_report', {
          target_report_id: reportId,
          new_status: 'dismissed'
        });
        if (error) throw error;
        
        setReports(prev => prev.filter(r => r.id !== reportId));
      } catch (err) {
        console.error('Dismiss report error:', err);
        showAlert('錯誤', '處理失敗');
      }
    });
  };

  const handleBanAndResolve = async (reportId, targetUserId) => {
    showConfirm('違規處置', '確認違規：確定要將該用戶停權，並結案這個檢舉嗎？', async () => {
      try {
        // 1. 停權用戶
        const { error: banError } = await supabase.rpc('admin_ban_user', {
          target_user_id: targetUserId,
          ban_status: true
        });
        if (banError) throw banError;

        // 2. 標記檢舉為已解決
        const { error: resolveError } = await supabase.rpc('admin_resolve_report', {
          target_report_id: reportId,
          new_status: 'resolved'
        });
        if (resolveError) throw resolveError;

        showAlert('成功', '用戶已停權，檢舉已結案');
        setReports(prev => prev.filter(r => r.id !== reportId));
      } catch (err) {
        console.error('Ban and resolve error:', err);
        showAlert('錯誤', '處理失敗');
      }
    });
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Loader2 className="animate-spin" /></div>;
  }

  if (reports.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '12px' }}>
        <CheckCircle size={48} style={{ color: '#10b981', margin: '0 auto 16px' }} />
        <h3 style={{ color: '#374151', fontSize: '1.25rem' }}>太棒了！目前沒有待處理的檢舉</h3>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {reports.map(report => (
        <div key={report.id} style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          borderLeft: '4px solid #f59e0b'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>檢舉人</p>
                <AvatarDisplay avatarId={report.reporter?.avatar || 'avatar1'} size={40} />
                <p style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '4px' }}>{report.reporter?.name}</p>
              </div>
              
              <div style={{ color: '#cbd5e1' }}>
                →
              </div>

              <div style={{ textAlign: 'center', position: 'relative' }}>
                <p style={{ fontSize: '12px', color: '#ef4444', marginBottom: '4px', fontWeight: 'bold' }}>被檢舉人</p>
                <AvatarDisplay avatarId={report.reported?.avatar || 'avatar1'} size={40} />
                <p style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '4px' }}>{report.reported?.name}</p>
                {report.reported?.is_banned && (
                  <span style={{ position: 'absolute', top: 20, right: -10, background: '#ef4444', color: 'white', fontSize: '10px', padding: '2px 4px', borderRadius: '4px' }}>已停權</span>
                )}
              </div>
            </div>

            <div style={{ background: '#f6f9fc', padding: '12px 16px', borderRadius: '12px', flex: 1, minWidth: '250px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <AlertTriangle size={16} color="#376ba1" />
                <strong style={{ color: '#b45309' }}>{report.reason}</strong>
              </div>
              <p style={{ color: '#78350f', fontSize: '14px', margin: 0, whiteSpace: 'pre-wrap' }}>
                {report.description || '(無詳細說明)'}
              </p>
              <p style={{ fontSize: '11px', color: '#376ba1', marginTop: '12px', textAlign: 'right' }}>
                {new Date(report.created_at).toLocaleString()}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <button
              onClick={() => handleDismiss(report.id)}
              style={{
                padding: '8px 20px',
                background: 'white',
                border: '1px solid #cbd5e1',
                color: '#64748b',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              忽略 (Dismiss)
            </button>
            <button
              onClick={() => handleBanAndResolve(report.id, report.reported_user_id)}
              disabled={report.reported?.is_banned}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 20px',
                background: report.reported?.is_banned ? '#fca5a5' : '#ef4444',
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: report.reported?.is_banned ? 'not-allowed' : 'pointer'
              }}
            >
              <Ban size={16} />
              {report.reported?.is_banned ? '已停權，結案' : '確認違規並封鎖 (Ban & Resolve)'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
